import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/services/api";
import {
  ArrowLeft, Clock, Target, Brain, Calculator, BookOpen,
  CheckCircle, XCircle, MinusCircle, FileText, AlertTriangle,
  BarChart3, ChevronLeft, ChevronRight, Timer, Zap, PenTool
} from "lucide-react";

const BankingMainsMockTestPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { toast } = useToast();

  const [phase, setPhase] = useState("select");
  const [loading, setLoading] = useState(false);
  const [patterns, setPatterns] = useState([]);

  const [sessionId, setSessionId] = useState(null);
  const [examName, setExamName] = useState("");
  const [questions, setQuestions] = useState([]);
  const [sectionMap, setSectionMap] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState({});
  const [negativeMarking, setNegativeMarking] = useState(0.25);
  const [hasDescriptive, setHasDescriptive] = useState(false);
  const [descriptiveMarks, setDescriptiveMarks] = useState(0);

  const [totalTime, setTotalTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);
  const [results, setResults] = useState(null);

  useEffect(() => { loadPatterns(); }, []);

  useEffect(() => {
    if (phase === "test" && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [phase]);

  const loadPatterns = async () => {
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/mains-patterns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPatterns(data.patterns || []);
    } catch { toast({ variant: "destructive", description: "Failed to load mains patterns" }); }
  };

  const startTest = async (examType) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/start-mains?exam_type=${examType}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setExamName(data.exam_name);
      setQuestions(data.questions);
      setSectionMap(data.section_map);
      setNegativeMarking(data.negative_marking);
      setHasDescriptive(data.has_descriptive);
      setDescriptiveMarks(data.descriptive_marks);
      setTotalTime(data.total_time_minutes * 60);
      setTimeLeft(data.total_time_minutes * 60);
      setCurrentQ(0);
      setAnswers({});
      setMarked({});
      setPhase("test");
      toast({ description: `${data.exam_name} started! ${data.total_questions} questions, ${data.total_time_minutes} min` });
    } catch { toast({ variant: "destructive", description: "Failed to start test" }); }
    finally { setLoading(false); }
  };

  const handleSubmit = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setLoading(true);
    try {
      const timeTaken = totalTime - timeLeft;
      const res = await fetch(`${API_BASE}/banking/mock-tests/submit/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          answers, questions, time_taken: timeTaken,
          negative_marking: negativeMarking, marks_per_question: 1
        })
      });
      const data = await res.json();
      setResults(data);
      setPhase("results");
    } catch { toast({ variant: "destructive", description: "Failed to submit" }); }
    finally { setLoading(false); }
  }, [sessionId, answers, questions, totalTime, timeLeft, negativeMarking, token]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const currentQuestion = questions[currentQ];

  // ─── SELECT PHASE ──────────────────────────────────────────────
  if (phase === "select") {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate("/banking")} data-testid="mains-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Mains Mock Tests</h1>
              <p className="text-sm text-slate-500">Full-length Mains exam simulation with sectional timing</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-blue-800">Mains Pattern: MCQ + Descriptive</p>
              <p className="text-sm text-blue-700">Mains exams have 4 MCQ sections + a descriptive paper (essay/letter). Negative marking: -0.25 per wrong MCQ answer.</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {patterns.map((p) => (
              <Card key={p.id} className="hover:shadow-lg transition-all border-slate-200 hover:border-blue-300" data-testid={`mains-exam-${p.id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-lg text-slate-900">{p.name}</h3>
                    <div className="flex gap-1">
                      <Badge className="bg-red-100 text-red-700 border-red-200">-0.25</Badge>
                      {p.has_descriptive && <Badge className="bg-blue-100 text-blue-700 border-blue-200"><PenTool className="w-3 h-3 mr-1" />Descriptive</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
                    <span className="flex items-center gap-1"><FileText className="w-4 h-4" /> {p.total_questions}Q</span>
                    <span className="flex items-center gap-1"><Target className="w-4 h-4" /> {p.total_marks} marks</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {p.total_time_minutes} min</span>
                  </div>
                  <div className="space-y-1 mb-3">
                    {p.sections.map((s) => (
                      <div key={s.id} className="flex justify-between text-xs text-slate-500">
                        <span>{s.name}</span>
                        <span>{s.questions}Q / {s.marks}M / {s.time_minutes}min</span>
                      </div>
                    ))}
                    {p.has_descriptive && (
                      <div className="flex justify-between text-xs text-blue-600 font-medium pt-1 border-t border-slate-100">
                        <span>Descriptive Paper</span>
                        <span>{p.descriptive_marks}M / {p.descriptive_time_minutes}min</span>
                      </div>
                    )}
                  </div>
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => startTest(p.id)}
                    disabled={loading}
                    data-testid={`start-mains-${p.id}`}
                  >
                    {loading ? "Loading..." : "Start Mains Mock"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {hasDescriptive && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-blue-700">After the MCQ section, you can practice the descriptive paper separately from the <strong>Descriptive Practice</strong> section.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // ─── TEST PHASE ────────────────────────────────────────────────
  if (phase === "test" && currentQuestion) {
    const timePercent = (timeLeft / totalTime) * 100;
    const isLowTime = timeLeft < 300;
    const attempted = Object.keys(answers).length;

    return (
      <div className="min-h-screen bg-slate-50">
        <div className={`sticky top-0 z-50 px-4 py-3 flex items-center justify-between border-b ${isLowTime ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-xs">{examName}</Badge>
            <span className="text-xs text-slate-500">Q {currentQ + 1}/{questions.length}</span>
          </div>
          <div className={`flex items-center gap-2 font-mono text-lg font-bold ${isLowTime ? 'text-red-600 animate-pulse' : 'text-slate-800'}`}>
            <Timer className="w-5 h-5" />
            {formatTime(timeLeft)}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{attempted}/{questions.length} done</span>
            <Button size="sm" variant="destructive" onClick={() => {
              if (window.confirm("Submit the test now?")) handleSubmit();
            }} data-testid="submit-mains-btn">Submit</Button>
          </div>
        </div>

        <div className="w-full h-1 bg-slate-200">
          <div className={`h-full transition-all ${isLowTime ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${timePercent}%` }} />
        </div>

        <div className="max-w-5xl mx-auto px-4 py-6 flex gap-6">
          <div className="hidden lg:block w-64 shrink-0">
            <Card className="sticky top-20">
              <CardContent className="p-4">
                <h4 className="text-xs font-semibold text-slate-500 uppercase mb-3">Navigator</h4>
                <div className="flex flex-wrap gap-1.5">
                  {questions.map((q, idx) => (
                    <button key={idx} onClick={() => setCurrentQ(idx)}
                      className={`w-8 h-8 rounded text-xs font-bold transition-all ${
                        currentQ === idx ? "ring-2 ring-blue-400 bg-blue-600 text-white"
                        : marked[q.id] ? "bg-amber-200 text-amber-800"
                        : answers[q.id] ? "bg-blue-100 text-blue-800"
                        : "bg-white text-slate-600 border border-slate-200"
                      }`}>{idx + 1}</button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex-1 space-y-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs">{currentQuestion.mock_section_name}</Badge>
                  <Badge variant="outline" className="text-xs capitalize">{currentQuestion.difficulty}</Badge>
                </div>
                <p className="text-lg text-slate-900 leading-relaxed mt-3 whitespace-pre-wrap">{currentQuestion.question}</p>

                <div className="space-y-3 mt-6">
                  {currentQuestion.options?.map((option) => {
                    const optKey = typeof option === 'object' ? option.key : option;
                    const optText = typeof option === 'object' ? option.text : option;
                    const isSelected = answers[currentQuestion.id] === optKey;
                    return (
                      <button key={optKey} onClick={() => setAnswers({ ...answers, [currentQuestion.id]: optKey })}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${isSelected ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-blue-300"}`}
                        data-testid={`mains-option-${optKey}`}>
                        <span className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isSelected ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-600"}`}>{optKey.toUpperCase()}</span>
                          <span className="text-slate-700">{optText}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}>
                      <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setCurrentQ(Math.min(questions.length - 1, currentQ + 1))} disabled={currentQ === questions.length - 1}>
                      Next <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { const { [currentQuestion.id]: _, ...rest } = answers; setAnswers(rest); }}>Clear</Button>
                    <Button variant={marked[currentQuestion.id] ? "default" : "outline"} size="sm"
                      className={marked[currentQuestion.id] ? "bg-amber-500 hover:bg-amber-600" : ""}
                      onClick={() => setMarked(prev => prev[currentQuestion.id] ? (() => { const { [currentQuestion.id]: _, ...r } = prev; return r; })() : { ...prev, [currentQuestion.id]: true })}>
                      {marked[currentQuestion.id] ? "Unmark" : "Mark"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ─── RESULTS PHASE ─────────────────────────────────────────────
  if (phase === "results" && results) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => { setPhase("select"); setResults(null); }}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-semibold text-slate-900">{examName} — Results</h1>
          </div>

          <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="p-8">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
                <div>
                  <p className="text-4xl font-bold text-blue-700">{results.total_marks}</p>
                  <p className="text-sm text-slate-600">Total Marks</p>
                  <p className="text-xs text-slate-400">out of {results.max_marks}</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-600 flex items-center justify-center gap-1"><CheckCircle className="w-6 h-6" /> {results.correct}</p>
                  <p className="text-sm text-slate-600">Correct</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-red-600 flex items-center justify-center gap-1"><XCircle className="w-6 h-6" /> {results.wrong}</p>
                  <p className="text-sm text-slate-600">Wrong</p>
                  <p className="text-xs text-red-600">-{results.negative_marks}</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-500 flex items-center justify-center gap-1"><MinusCircle className="w-6 h-6" /> {results.unattempted}</p>
                  <p className="text-sm text-slate-600">Unattempted</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">{results.percentage}%</p>
                  <p className="text-sm text-slate-600">Score</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {results.section_results && Object.keys(results.section_results).length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Section-wise Performance</h3>
                <div className="space-y-3">
                  {Object.entries(results.section_results).map(([secId, sec]) => (
                    <div key={secId} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <p className="font-medium text-slate-900 capitalize">{secId.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-slate-500">{sec.correct}C / {sec.wrong}W / {sec.unattempted}S</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-slate-900">{sec.marks_obtained}/{sec.max_marks}</p>
                        <p className="text-xs text-slate-500">{sec.accuracy}% acc</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-3">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setPhase("select"); setResults(null); }}>
              Take Another Test
            </Button>
            <Button variant="outline" onClick={() => navigate("/banking/descriptive")}>
              Practice Descriptive Paper
            </Button>
            <Button variant="outline" onClick={() => navigate("/banking")}>
              Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BankingMainsMockTestPage;
